import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LayoutService } from '@app/shared/components/layout/layout.service';
import { ToastService } from '@teorema/patron';
import { Subscription } from 'rxjs';
import { AccessType, BwUser, bwUserActiveFilter } from 'src/app/models/bw-user.model';
import { FindOptions } from 'src/app/models/find-options.model';
import { Module, Profile } from 'src/app/models/user.model';
import { ProfileService, utils } from 'src/app/shared';
import { PipelineAccessComponent } from 'src/app/features/ai/components/pipeline-access/pipeline-access.component';

@Component({
  templateUrl: './profile-form.component.html',
  styleUrls: ['./profile-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ProfileFormComponent implements OnInit, OnDestroy {
  profile: Profile = new Profile();
  systemGroups?: any[] = [];
  sub?: Subscription;
  loading: boolean = false;
  formGroup: FormGroup;
  filterModulesText: string = '';
  formModules: any[] = [];
  loadingBwUsers: boolean = false;
  bwUsers?: BwUser[];
  filterUsersText: string = '';
  bwUsersFindOptions: FindOptions = {
    orderBy: 'name',
    fields: [
      { valueField: 'name', label: 'Nome', highlight: true },
      { valueField: 'email', label: 'E-mail', highlight: true },
    ],
    filters: [bwUserActiveFilter(false)],
  };

  constructor(
    private activatedRoute: ActivatedRoute,
    private profileService: ProfileService,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private layoutService: LayoutService
  ) {
    this.formGroup = this.formBuilder.group({
      name: [null],
      type: [null],
      observation: false,
      attachments: false,
    });
  }

  async ngOnInit() {
    await this.findModules();
    this.sub = this.activatedRoute.params.subscribe((params: any) => {
      if (params?.id) {
        this.findProfile(params.id);
      } else {
        this.profile.active = true;
        this.profile.type = 'U';
        this.profile.modules = [];
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  async findProfile(profileId: string) {
    this.loading = true;

    const response: any = await this.profileService.findById(profileId).finally(() => {
      this.loading = false;
    });
    if (response?.id) {
      this.profile = response;
      this.updateForm();
      this.updateSelectAllValues();
      this.findUsers();
    }
  }

  async findModules() {
    this.loading = true;
    await this.profileService
      .getSystemModules()
      .then((response: any) => {
        this.systemGroups = response?.data;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  async findUsers() {
    if (!this.profile.id) return;

    this.loadingBwUsers = true;
    await this.profileService
      .listBwUsers(this.profile.id)
      .then((response: any) => {
        this.bwUsers = response ?? [];
      })
      .finally(() => {
        this.loadingBwUsers = false;
      });
  }

  updateForm() {
    this.formGroup.patchValue({
      name: this.profile.name,
      type: this.profile.type,
      observation: this.profile.requirements?.order?.observation ?? false,
      attachments: this.profile.requirements?.order?.attachments ?? false,
    });

    this.systemModules?.forEach(m => {
      if (!this.profile?.modules?.find(i => i.module_id == m.module_id)) {
        this.profile?.modules?.push(new Module(m.module_id));
      }
    });
  }

  get systemModules() {
    let modules: any[] = [];
    this.systemGroups?.forEach(g => {
      modules = modules.concat(g.modules);
    });
    return modules;
  }

  getModule(module: any) {
    return this.profile?.modules?.find(i => i.module_id == module.module_id) ?? new Module();
  }

  getFormModules() {
    if (!this.profileAccessType) return [];
    const result: any = [];

    const systemGroups: any[] = JSON.parse(JSON.stringify(this.systemGroups));

    systemGroups?.forEach(g => {
      g.modules = g.modules?.filter((i: any) => {
        return (
          i.can_read?.includes(this.profileAccessType) ||
          i.can_write?.includes(this.profileAccessType) ||
          i.can_execute.includes(this.profileAccessType) ||
          i.can_destroy?.includes(this.profileAccessType)
        );
      });

      if (g.modules?.length) {
        result.push(g);
      }
    });

    return result;
  }

  modulesFilter(modules: any[]) {
    return;
  }

  get profileAccessType() {
    const { type } = this.formGroup.value;
    let accessType: string | null = null;
    if (type == 'U') accessType = AccessType.User;
    if (type == 'C') accessType = AccessType.Customer;
    if (type == 'S') accessType = AccessType.Supplier;
    return accessType;
  }

  @ViewChild(PipelineAccessComponent) pipelineAccess?: PipelineAccessComponent;

  async save() {
    if (this.loading) return;
    this.loading = true;
    const { name, type, observation, attachments } = this.formGroup.value;
    const modules: Module[] = [];
    this.profile?.modules?.forEach(m => {
      if (m?.id || m?.can_read || m?.can_write || m?.can_destroy) {
        modules.push(m);
      }
    });

    this.profile = {
      ...this.profile!,
      name,
      type,
      modules,
      requirements: {
        order: {
          observation,
          attachments,
        },
      },
    };

    const result = await this.profileService.save(this.profile).catch(err => {
      if (!err?.messageShown)
        this.toastService.show({ color: 'error', message: 'Houve um erro ao salvar o perfil' });
    });

    if (result) {
      this.toastService.show({ color: 'success', message: 'Salvo com sucesso' });
      this.profile = result;
      this.updateForm();
      // A aba Pipelines salva por endpoint próprio; o Salvar do topo cobre as duas.
      await this.pipelineAccess?.saveIfDirty();
    }

    this.loading = false;

    const user = utils.getUser();
    const index = user.bw_user_profiles?.findIndex(i => i.id === result?.id);
    if (index != null && index >= 0) {
      this.layoutService.callUpdateNavItems.emit();
    }
  }

  isSelectedAll: {
    can_read?: boolean;
    can_write?: boolean;
    can_execute?: boolean;
    can_destroy?: boolean;
  } = {};

  isSelectedGroup: any = {};

  updateSelectAllValues(changeProfileAccessType = false) {
    let allModules: any[] = [];
    if (!this.formModules.length || changeProfileAccessType) {
      this.formModules = this.getFormModules();
    }

    this.formModules?.forEach((f: any) => {
      allModules = allModules.concat(f.modules);
      if (!this.isSelectedGroup[f.group]) this.isSelectedGroup[f.group] = {};

      if (f.modules.some((m: any) => m.can_read.includes(this.profileAccessType)))
        this.isSelectedGroup[f.group].can_read =
          !f.modules.some((m: any) => !this.getModule(m).can_read) || false;
      if (f.modules.some((m: any) => m.can_write.includes(this.profileAccessType)))
        this.isSelectedGroup[f.group].can_write =
          !f.modules.some((m: any) => !this.getModule(m).can_write) || false;
      if (f.modules.some((m: any) => m.can_execute.includes(this.profileAccessType)))
        this.isSelectedGroup[f.group].can_execute =
          !f.modules.some((m: any) => !this.getModule(m).can_execute) || false;
      if (f.modules.some((m: any) => m.can_destroy?.includes(this.profileAccessType)))
        this.isSelectedGroup[f.group].can_destroy =
          !f.modules.some(
            (m: any) =>
              m.can_destroy?.includes(this.profileAccessType) && !this.getModule(m).can_destroy
          ) || false;
    });

    if (allModules.some((m: any) => m.can_read.includes(this.profileAccessType)))
      this.isSelectedAll.can_read = !this.profile.modules?.length
        ? false
        : !this.profile.modules?.some(m => !m.can_read) || false;
    if (allModules.some((m: any) => m.can_write.includes(this.profileAccessType)))
      this.isSelectedAll.can_write = !this.profile.modules?.length
        ? false
        : !this.profile.modules?.some(m => !m.can_write) || false;
    if (allModules.some((m: any) => m.can_execute.includes(this.profileAccessType)))
      this.isSelectedAll.can_execute = !this.profile.modules?.length
        ? false
        : !this.profile.modules?.some(m => !m.can_execute) || false;
    if (allModules.some((m: any) => m.can_destroy?.includes(this.profileAccessType)))
      this.isSelectedAll.can_destroy = !this.profile.modules?.length
        ? false
        : !this.profile.modules?.some(m => !m.can_destroy) || false;
  }

  selectAllToggle(event: boolean, type: string, group?: string) {
    let groups = this.formModules;
    if (group) groups = this.formModules?.filter((item: any) => item.group == group);

    let modules =
      groups?.reduce((accumulator: string | any[], group: { modules: any }) => {
        return accumulator.concat(group.modules);
      }, []) ?? [];

    modules.forEach((module: any) => {
      if (<Array<string>>module[type].includes(this.profileAccessType)) {
        const m: any = this.getModule(module);
        m[type] = event ? 1 : 0;
        if (!this.profile.modules?.find(m => m.module_id == module.module_id)) {
          this.profile.modules?.push({ ...m, module_id: module.module_id });
        }
      }
    });

    if (!group) {
      Object.values(this.isSelectedGroup).forEach((group: any) => {
        if (group[type] != undefined) group[type] = event;
      });
    }

    this.updateSelectAllValues();
  }

  saveBwUsers(ids: string[]) {
    this.loadingBwUsers = true;
    this.profileService
      .saveBwUsers(this.profile.id, ids)
      .then((response: any) => {
        if (response) {
          this.bwUsers = response ?? [];
          this.toastService.show({ color: 'success', message: 'Salvo com sucesso' });
        }
      })
      .catch(err => {
        if (!err?.messageShown)
          this.toastService.show({
            color: 'error',
            message: 'Houve um erro ao salvar os usuários',
          });
      })
      .finally(() => {
        this.loadingBwUsers = false;
      });
  }

  /** Usuários do perfil filtrados pelo texto (nome ou e-mail), como o filtro da aba Módulos. */
  get filteredBwUsers(): BwUser[] {
    const filter = utils.normalize(this.filterUsersText);
    if (!filter) return this.bwUsers ?? [];
    return (this.bwUsers ?? []).filter(user =>
      utils.normalize(`${user.name ?? ''} ${user.email ?? ''}`).includes(filter)
    );
  }

  get bwUsersIds() {
    return this.bwUsers?.map((user: any) => user.id) ?? [];
  }
}
